// Copyright 2025, University of Maryland, All Rights Reserved
import { forwardRef, useImperativeHandle, useCallback, useState, useMemo, useEffect } from 'react'
import type { ReactNode } from 'react'
import { DndContext, DragOverlay, useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Layers, MousePointerClick, Pencil, Plus, X } from 'lucide-react'
import {
  Badge,
  Button,
  Input,
  Tooltip,
  TooltipTrigger,
  DarkTooltipContent,
  TooltipProvider,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Label,
  cn,
} from '@/components/ui'
import { ConfirmDialog } from '@/components/forms'
import { getServices } from '@/app-shell/services'
import { toast } from 'sonner'
import { ConfigurationPanel, ToolPalette } from '@/subsystems/palette'
import type { PipelineNodeData } from '../../../core/contracts'
import type { Node as FlowNode } from '@xyflow/react'
import type { UILayout as WorkspaceLayout } from '../types'
import { useBuilderLayout } from '../hooks/useBuilderLayout'
import { toApiLayout } from '../utils'
import type { BuilderWidget } from '../types'
import { useWidgetDnD } from '../hooks/useWidgetDnD'
import { SortableWidgetItem } from './SortableWidgetItem'
import { getLayoutComponent } from '../../../core/layout-registry'

interface LayoutBuilderProps {
  organizationId?: string
  workspaceId?: string
  layout?: WorkspaceLayout | null
  onSave?: (layout: WorkspaceLayout) => Promise<WorkspaceLayout | void>
}

export interface LayoutBuilderHandle {
  exitInlineAdmin: (options?: { force?: boolean }) => boolean
}

const DroppableTab: React.FC<{ tabId: string; children: ReactNode }> = ({ tabId, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: tabId, data: { tabId } })
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'overflow-hidden rounded-xl border bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)] transition-colors duration-150',
        isOver ? 'border-blue-300' : 'border-[#e7ecf2]',
      )}
    >
      {children}
    </div>
  )
}

export const LayoutBuilder = forwardRef<LayoutBuilderHandle, LayoutBuilderProps>(({
  organizationId,
  workspaceId,
  layout: initialLayout,
  onSave,
}, ref) => {
  const [configuringWidget, setConfiguringWidget] = useState<{ tabId: string; widget: BuilderWidget } | null>(null)
  const [pendingWidget, setPendingWidget] = useState<{ tabId: string; componentId: string } | null>(null)
  const [pendingInstanceId, setPendingInstanceId] = useState('')
  const [autoSavePending, setAutoSavePending] = useState(false)
  const [mobilePaletteOpen, setMobilePaletteOpen] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState<
    | { type: 'tab'; tabId: string; label: string }
    | { type: 'widget'; tabId: string; widget: BuilderWidget }
    | null
  >(null)
  const [removeLoading, setRemoveLoading] = useState(false)

  const {
    layout,
    tabs,
    addTab,
    removeTab,
    updateTab,
    removeWidgetFromTab,
    setDefaultTab,
  } = useBuilderLayout({ initialLayout })

  const [activeTabId, setActiveTabId] = useState<string | null>(
    () => layout.defaultTab ?? tabs[0]?.id ?? null,
  )

  const closeConfiguration = useCallback(() => {
    setConfiguringWidget(null)
  }, [])

  const handleConfigurationSave = useCallback((nextData: PipelineNodeData) => {
    if (!configuringWidget) {
      return
    }

    updateTab(configuringWidget.tabId, tab => ({
      ...tab,
      widgets: tab.widgets.map(widget =>
        widget.id === configuringWidget.widget.id
          ? {
              ...widget,
              label: nextData.label || widget.label,
              instanceId: nextData.instanceId ?? widget.instanceId,
            }
          : widget,
      ),
    }))

    setConfiguringWidget(null)
  }, [configuringWidget, updateTab])

  // Tab rename editing state
  const [editingTab, setEditingTab] = useState<{ id: string; label: string } | null>(null)

  useEffect(() => {
    if (activeTabId && tabs.some(tab => tab.id === activeTabId)) {
      return
    }
    setActiveTabId(tabs[0]?.id ?? null)
  }, [tabs, activeTabId])

  const activeTab = useMemo(
    () => (activeTabId ? tabs.find(tab => tab.id === activeTabId) ?? null : null),
    [tabs, activeTabId],
  )

  const setTabWidgets = useCallback(
    (tabId: string, updater: (widgets: BuilderWidget[]) => BuilderWidget[]) => {
      updateTab(tabId, tab => ({
        ...tab,
        widgets: updater(tab.widgets).map((widget, index) => ({
          ...widget,
          order: index,
        })),
      }))
    },
    [updateTab],
  )

  const createWidgetFromNode = useCallback((tabId: string, componentId: string, instanceId?: string): BuilderWidget => {
    const layoutComponent = getLayoutComponent(componentId)
    const label = layoutComponent?.display.label ?? componentId
    const widgetId = `${tabId}-${componentId}-${Date.now()}`

    return {
      id: widgetId,
      order: 0,
      component: componentId,
      label,
      instanceId: instanceId ?? undefined,
    }
  }, [])

  const addWidgetFromNode = useCallback((tabId: string, nodeDefId: string) => {
    const layoutComponent = getLayoutComponent(nodeDefId)
    if (layoutComponent?.parentType === 'subsystem') {
      setPendingWidget({ tabId, componentId: nodeDefId })
      setPendingInstanceId('')
      return
    }
    const widget = createWidgetFromNode(tabId, nodeDefId)
    setTabWidgets(tabId, widgets => [...widgets, widget])
    setAutoSavePending(true)
  }, [createWidgetFromNode, setTabWidgets])

  // Tap-to-add: palette items click straight into the active tab, which is the
  // only add path on touch screens where drag isn't practical from the palette.
  const handlePaletteSelect = useCallback((item: { id: string }) => {
    if (!activeTabId) {
      toast.info('Add a tab first, then add components to it')
      return
    }
    addWidgetFromNode(activeTabId, item.id)
    setMobilePaletteOpen(false)
  }, [activeTabId, addWidgetFromNode])

  const confirmPendingWidget = useCallback(async () => {
    if (!pendingWidget || !pendingInstanceId.trim()) return
    const instanceId = pendingInstanceId.trim()

    // Create backend instance for knowledge base widgets
    if (pendingWidget.componentId === 'knowledge_base' && organizationId && workspaceId) {
      try {
        const client = getServices().getClient()
        await client.post(
          `/org/${organizationId}/ws/${workspaceId}/knowledge_base/instances`,
          { instanceId, label: 'Knowledge Base' },
        )
      } catch (err) {
        console.error('[LayoutBuilder] Failed to create knowledge base instance', err)
        toast.error('Failed to create knowledge base instance')
        return
      }
    }

    const widget = createWidgetFromNode(pendingWidget.tabId, pendingWidget.componentId, instanceId)
    setTabWidgets(pendingWidget.tabId, widgets => [...widgets, widget])
    setPendingWidget(null)
    setPendingInstanceId('')
    setAutoSavePending(true)
  }, [pendingWidget, pendingInstanceId, createWidgetFromNode, setTabWidgets, organizationId, workspaceId])

  const cancelPendingWidget = useCallback(() => {
    setPendingWidget(null)
    setPendingInstanceId('')
  }, [])

  const moveWidget = useCallback((sourceTabId: string, targetTabId: string, widget: BuilderWidget, overId: string | null) => {
    if (sourceTabId === targetTabId && overId === widget.id) {
      return
    }

    setTabWidgets(sourceTabId, widgets => widgets.filter(w => w.id !== widget.id))
    setTabWidgets(targetTabId, widgets => {
      const next = [...widgets]
      const targetIndex = overId ? next.findIndex(w => w.id === overId) : -1
      const insertIndex = targetIndex >= 0 ? targetIndex : next.length
      next.splice(insertIndex, 0, { ...widget, order: insertIndex })
      return next.map((w, index) => ({ ...w, order: index }))
    })
  }, [setTabWidgets])

  const { activeDragItem, dndContextProps } = useWidgetDnD({
    addWidgetFromNode,
    moveWidget,
  })

  const handleSave = useCallback(async () => {
    if (!onSave) {
      return
    }
    await onSave(toApiLayout(layout))
  }, [layout, onSave])

  useEffect(() => {
    if (!autoSavePending) return
    setAutoSavePending(false)
    void handleSave()
  }, [autoSavePending, handleSave])

  useImperativeHandle(ref, () => ({
    exitInlineAdmin: () => true,
  }))

  const handleAddTab = useCallback(() => {
    const nextIndex = tabs.length + 1
    const newTab = addTab({ label: `Tab ${nextIndex}` })
    setActiveTabId(newTab.id)
    setEditingTab({ id: newTab.id, label: newTab.label })
    setAutoSavePending(true)
  }, [addTab, tabs.length])

  const requestRemoveTab = useCallback((tabId: string) => {
    const tab = tabs.find(t => t.id === tabId)
    setConfirmRemove({ type: 'tab', tabId, label: tab?.label ?? tabId })
  }, [tabs])

  const requestRemoveWidget = useCallback((tabId: string, widget: BuilderWidget) => {
    setConfirmRemove({ type: 'widget', tabId, widget })
  }, [])

  const deleteKbInstances = useCallback(async (widgets: BuilderWidget[]) => {
    if (!organizationId || !workspaceId) return true
    const kbWidgets = widgets.filter(w => w.component === 'knowledge_base' && w.instanceId)
    if (kbWidgets.length === 0) return true

    const client = getServices().getClient()
    for (const w of kbWidgets) {
      try {
        await client.delete(
          `/org/${organizationId}/ws/${workspaceId}/knowledge_base/instances/${w.instanceId}`
        )
      } catch (err) {
        console.error('[LayoutBuilder] Failed to delete knowledge base instance', err)
        toast.error(`Failed to delete knowledge base instance "${w.instanceId}"`)
        return false
      }
    }
    return true
  }, [organizationId, workspaceId])

  const handleConfirmRemove = useCallback(async () => {
    if (!confirmRemove) return

    setRemoveLoading(true)

    if (confirmRemove.type === 'tab') {
      const { tabId } = confirmRemove
      const tab = tabs.find(t => t.id === tabId)
      if (tab && !(await deleteKbInstances(tab.widgets))) {
        setRemoveLoading(false)
        setConfirmRemove(null)
        return
      }
      const remaining = tabs.filter(t => t.id !== tabId)
      removeTab(tabId)
      if (activeTabId === tabId) {
        setActiveTabId(remaining[0]?.id ?? null)
      }
    } else {
      const { tabId, widget } = confirmRemove
      if (!(await deleteKbInstances([widget]))) {
        setRemoveLoading(false)
        setConfirmRemove(null)
        return
      }
      removeWidgetFromTab(tabId, widget.id)
    }

    setRemoveLoading(false)
    setConfirmRemove(null)
    setAutoSavePending(true)
  }, [confirmRemove, tabs, removeTab, activeTabId, removeWidgetFromTab, deleteKbInstances])

  const commitTabRename = useCallback(() => {
    if (!editingTab) return
    const trimmed = editingTab.label.trim()
    const current = tabs.find(t => t.id === editingTab.id)
    if (trimmed && trimmed !== current?.label) {
      updateTab(editingTab.id, tab => ({ ...tab, label: trimmed }))
      setAutoSavePending(true)
    }
    setEditingTab(null)
  }, [editingTab, tabs, updateTab])

  const renderActiveTab = () => {
    if (!activeTab) {
      return (
        <div className="flex h-full min-h-[280px] items-center justify-center rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
          <div className="max-w-sm px-6 text-center animate-in fade-in duration-200">
            <p className="text-[13.5px] font-[550] text-slate-700">No tabs yet</p>
            <p className="mt-1 text-[12.5px] text-slate-400">
              Tabs organize the components your workspace members see.
            </p>
            <Button size="sm" className="mt-4" onClick={handleAddTab}>
              <Plus className="mr-1 h-4 w-4" />
              Add Tab
            </Button>
          </div>
        </div>
      )
    }

    const isDefault = layout.defaultTab === activeTab.id

    return (
      <TooltipProvider delayDuration={300}>
      <DroppableTab tabId={activeTab.id}>
        <div className="flex items-center justify-between gap-3 border-b border-[#eef2f6] px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="truncate text-sm font-semibold tracking-[-0.01em] text-slate-900">
              {activeTab.label}
            </span>
            {isDefault && (
              <Badge variant="secondary" className="text-[11px] font-medium text-slate-500">
                Default tab
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-xs tabular-nums text-slate-400 sm:inline">
              {activeTab.widgets.length} component{activeTab.widgets.length === 1 ? '' : 's'}
            </span>
            {!isDefault && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-[13px] text-slate-500 hover:text-slate-800"
                onClick={() => { setDefaultTab(activeTab.id); setAutoSavePending(true) }}
              >
                Set as default
              </Button>
            )}
          </div>
        </div>

        <SortableContext items={activeTab.widgets.map(widget => widget.id)} strategy={verticalListSortingStrategy}>
          <div key={activeTab.id} className="space-y-2 p-4 animate-in fade-in duration-200">
            {activeTab.widgets.map(widget => (
              <SortableWidgetItem
                key={widget.id}
                widget={widget}
                tabId={activeTab.id}
                onConfigure={() => setConfiguringWidget({ tabId: activeTab.id, widget })}
                onRemove={() => requestRemoveWidget(activeTab.id, widget)}
              />
            ))}
            {activeTab.widgets.length === 0 && (
              <div className="flex min-h-[180px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/50 px-6 text-center">
                <MousePointerClick className="h-5 w-5 text-slate-300" />
                <p className="text-[13px] font-[550] text-slate-500">This tab is empty</p>
                <p className="text-[12.5px] text-slate-400">
                  Drag a component here, or click one in the palette to add it.
                </p>
              </div>
            )}
          </div>
        </SortableContext>
      </DroppableTab>
      </TooltipProvider>
    )
  }

  if (configuringWidget) {
    const { widget, tabId } = configuringWidget
    const layoutComponent = getLayoutComponent(widget.component)
    const tabLabel = tabs.find(t => t.id === tabId)?.label

    // Derive display label once for both header and node data
    const widgetTitle = widget.label ?? layoutComponent?.display.label ?? widget.component

    const node: FlowNode<PipelineNodeData> = {
      id: widget.id,
      type: widget.component,
      position: { x: 0, y: 0 },
      data: {
        label: widgetTitle,
        nodeDefinition: widget.component,
        instanceId: widget.instanceId ?? undefined,
        configured: Boolean(widget.instanceId),
      },
    }

    return (
      <div className="flex h-full w-full flex-col">
        <div className="border-b border-[#eef2f6] bg-white px-5 py-4 sm:px-8">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="truncate text-[17px] font-[650] tracking-[-0.015em] text-slate-900">{widgetTitle}</p>
              {tabLabel && (
                <p className="mt-0.5 text-[13px] text-slate-500">
                  Configured on tab · {tabLabel}
                </p>
              )}
            </div>
            <Button variant="outline" size="sm" className="flex-shrink-0" onClick={closeConfiguration}>
              Back to Layout Builder
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <ConfigurationPanel
            node={node}
            onClose={closeConfiguration}
            onUpdate={(data) => handleConfigurationSave(data as PipelineNodeData)}
            organizationId={organizationId}
            workspaceId={workspaceId}
          />
        </div>
      </div>
    )
  }

  return (
    <DndContext {...dndContextProps}>
      <TooltipProvider delayDuration={300}>
      <div className="flex h-full w-full flex-col">
        <div className="border-b border-[#eef2f6] bg-white px-4 py-2.5">
          <div className="flex items-center gap-2">
            <div className="flex flex-1 items-center gap-1 overflow-x-auto">
              {tabs.map(tab => (
                <div key={tab.id} className="flex flex-shrink-0 items-center">
                  {editingTab?.id === tab.id ? (
                    <Input
                      value={editingTab.label}
                      onChange={e => setEditingTab({ ...editingTab, label: e.target.value })}
                      onBlur={commitTabRename}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitTabRename()
                        if (e.key === 'Escape') setEditingTab(null)
                      }}
                      className="h-8 w-48 text-sm sm:w-56"
                      autoFocus
                      data-testid="builder-layout-tab-name"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setActiveTabId(tab.id)}
                      className={cn(
                        'inline-flex h-8 items-center rounded-md px-3 text-[13px] font-medium transition-colors duration-150',
                        activeTabId === tab.id
                          ? 'bg-slate-900 text-white shadow-sm'
                          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                      )}
                    >
                      {tab.label}
                    </button>
                  )}
                  {editingTab?.id !== tab.id && (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => setEditingTab({ id: tab.id, label: tab.label })}
                            className="ml-0.5 inline-flex h-6 w-6 items-center justify-center rounded text-slate-300 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-600"
                          >
                            <Pencil className="h-3 w-3" />
                            <span className="sr-only">Rename {tab.label}</span>
                          </button>
                        </TooltipTrigger>
                        <DarkTooltipContent>Rename tab</DarkTooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => requestRemoveTab(tab.id)}
                            data-testid="builder-layout-delete-tab"
                            className="ml-0.5 inline-flex h-6 w-6 items-center justify-center rounded text-slate-300 transition-colors duration-150 hover:bg-slate-100 hover:text-red-600"
                          >
                            <X className="h-3.5 w-3.5" />
                            <span className="sr-only">Remove {tab.label}</span>
                          </button>
                        </TooltipTrigger>
                        <DarkTooltipContent>Remove tab</DarkTooltipContent>
                      </Tooltip>
                    </>
                  )}
                </div>
              ))}

              <Button
                onClick={handleAddTab}
                size="sm"
                variant="ghost"
                className="h-8 flex-shrink-0 gap-1.5 px-2.5 text-[13px] text-slate-500 hover:text-slate-800"
                data-testid="builder-layout-add-tab"
              >
                <Plus className="h-4 w-4" />
                Add Tab
              </Button>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="h-8 flex-shrink-0 gap-1.5 text-[13px] md:hidden"
              aria-expanded={mobilePaletteOpen}
              onClick={() => setMobilePaletteOpen(open => !open)}
            >
              <Layers className="h-4 w-4" />
              Components
            </Button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:flex-row md:overflow-hidden">
          <div
            data-testid="builder-node-palette"
            className={cn(
              'flex-shrink-0 overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]',
              'md:block md:h-auto md:w-72',
              mobilePaletteOpen
                ? 'h-[45vh] animate-in fade-in slide-in-from-top-2 duration-200'
                : 'hidden',
            )}
          >
            <ToolPalette viewMode="layout" onNodeSelect={handlePaletteSelect} />
          </div>

          <div className="min-w-0 flex-1 md:overflow-y-auto">
            {renderActiveTab()}
          </div>
        </div>

      </div>
      </TooltipProvider>

      <DragOverlay>
        {activeDragItem && (
          <div className="rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-[13px] font-[550] text-slate-700 shadow-lg">
            {activeDragItem.type === 'builder-widget'
              ? activeDragItem.widget.label
              : getLayoutComponent(activeDragItem.nodeId)?.display.label ?? activeDragItem.nodeId}
          </div>
        )}
      </DragOverlay>

      <Dialog open={Boolean(pendingWidget)} onOpenChange={open => { if (!open) cancelPendingWidget() }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingWidget ? (getLayoutComponent(pendingWidget.componentId)?.display.label ?? pendingWidget.componentId) : 'Widget'}
            </DialogTitle>
            <DialogDescription>
              Enter an instance identifier for this widget. This links the widget to its data on the backend.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="widget-instance-id">Instance ID</Label>
            <Input
              id="widget-instance-id"
              value={pendingInstanceId}
              onChange={e => setPendingInstanceId(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') confirmPendingWidget() }}
              placeholder="e.g. knowledge_base.contracts"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelPendingWidget}>Cancel</Button>
            <Button onClick={confirmPendingWidget} disabled={!pendingInstanceId.trim()}>Add Widget</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirmRemove)}
        onOpenChange={open => { if (!open) setConfirmRemove(null) }}
        title={
          confirmRemove?.type === 'tab'
            ? `Remove tab "${confirmRemove.label}"?`
            : confirmRemove?.type === 'widget'
              ? `Remove ${confirmRemove.widget.label ?? confirmRemove.widget.component}?`
              : 'Confirm removal'
        }
        description={
          confirmRemove?.type === 'tab'
            ? tabs.find(t => t.id === confirmRemove.tabId)?.widgets.some(w => w.component === 'knowledge_base' && w.instanceId)
              ? 'This will remove the tab and all its widgets, and permanently delete any knowledge base data.'
              : 'This will remove the tab and all its widgets from the layout.'
            : confirmRemove?.type === 'widget' && confirmRemove.widget.component === 'knowledge_base' && confirmRemove.widget.instanceId
              ? 'This will remove the widget from the layout and permanently delete its knowledge base data.'
              : 'This will remove the widget from the layout.'
        }
        confirmLabel="Remove"
        variant="destructive"
        loading={removeLoading}
        onConfirm={handleConfirmRemove}
        onCancel={() => setConfirmRemove(null)}
      />
    </DndContext>
  )
})

LayoutBuilder.displayName = 'LayoutBuilder'
